import React, { memo } from "react";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";
import Text from "@components/CustomText";
import ChevronRight from "@assets/svg/ChevronRight";
import Tick from "@assets/svg/Tick";
import { ATTENDANCE_COLORS as C } from "./constants";

interface Props {
  badge: string;
  title: string;
  subtitle: string;
  marked: boolean;
  onPress: () => void;
  disabled?: boolean;
  /** Shows a spinner in place of the chevron and blocks presses. */
  busy?: boolean;
  testID?: string;
}

/** Full-width coloured card: red while pending, green once marked. Used for role tiles and IN/OUT. */
const RoleTile = ({
  badge,
  title,
  subtitle,
  marked,
  onPress,
  disabled = false,
  busy = false,
  testID,
}: Props) => (
  <Pressable
    testID={testID}
    accessibilityRole="button"
    accessibilityLabel={`${title}. ${subtitle}`}
    accessibilityState={{ disabled, busy }}
    disabled={disabled || busy}
    onPress={onPress}
    style={({ pressed }) => [
      styles.tile,
      { backgroundColor: marked ? C.marked : C.pending },
      (pressed || disabled) && styles.dim,
    ]}
  >
    <View style={styles.left}>
      <View style={styles.badge}>
        {marked ? (
          <Tick width={22} height={22} strokeColor={C.white} />
        ) : (
          <Text style={styles.badgeText}>{badge}</Text>
        )}
      </View>
      <View style={styles.texts}>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.subtitle}>{subtitle}</Text>
      </View>
    </View>
    {busy ? (
      <ActivityIndicator color={C.white} />
    ) : (
      <ChevronRight width={12} height={16} pathFill={C.onColorSoft} />
    )}
  </Pressable>
);

export default memo(RoleTile);

const styles = StyleSheet.create({
  tile: {
    borderRadius: 14,
    padding: 18,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  dim: { opacity: 0.6 },
  left: { flexDirection: "row", alignItems: "center", flex: 1 },
  badge: {
    width: 44,
    height: 44,
    borderRadius: 11,
    backgroundColor: C.badgeBg,
    alignItems: "center",
    justifyContent: "center",
  },
  badgeText: { color: C.white, fontSize: 12, fontWeight: "700" },
  texts: { marginLeft: 14, flex: 1 },
  title: { color: C.white, fontSize: 17, fontWeight: "700" },
  subtitle: { color: C.onColorSoft, fontSize: 13, marginTop: 2 },
});
